import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "./i18n";
import { analytics } from "./utils/analytics";

interface MobileOS {
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
}

function App() {
  const { t, language } = useTranslation();
  const [schoolSku, setSchoolSku] = useState<string>("");
  const [schoolName, setSchoolName] = useState<string>("");
  const [mobileOS, setMobileOS] = useState<MobileOS>({
    isIOS: false,
    isAndroid: false,
    isMobile: false,
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split("/").filter(Boolean);

    const sku = pathParts[0] || "demo";

    // Default school name based on language
    const defaultNames: Record<string, string> = {
      en: "Demo School",
      pt: "Escola Demo",
      it: "Scuola Demo",
      de: "Demo-Schule",
      fr: "École Démo",
      es: "Escuela Demo",
    };

    const name =
      urlParams.get("name") || defaultNames[language] || "Demo School";

    setSchoolSku(sku);
    setSchoolName(name);
    analytics.qrPageView(sku, name);

    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;
    const isIOS =
      /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    const isAndroid = /android/i.test(userAgent);
    const isMobile = isIOS || isAndroid;

    setMobileOS({ isIOS, isAndroid, isMobile });

    document.title = `${name} - Agenda1`;
  }, [language]);

  // Use the current URL so QR code works with any domain (CloudFront or custom domain)
  const deepLink = window.location.href;

  // Build the app deeplink URL for opening the school
  // Custom scheme format: agenda1://{schoolSku}?name=...&lang=...
  // The app parses the hostname as the school SKU
  const customSchemeLink = `agenda1://${schoolSku}?name=${encodeURIComponent(schoolName)}&lang=${language}`;

  // Localized App Store links
  const appStoreLinks: Record<string, string> = {
    en: "https://apps.apple.com/us/app/agenda1-agenda-escolar/id1194790287",
    pt: "https://apps.apple.com/br/app/agenda1-agenda-escolar/id1194790287",
    it: "https://apps.apple.com/it/app/agenda1-agenda-escolar/id1194790287",
    de: "https://apps.apple.com/de/app/agenda1-agenda-escolar/id1194790287",
    fr: "https://apps.apple.com/fr/app/agenda1-agenda-escolar/id1194790287",
    es: "https://apps.apple.com/es/app/agenda1-agenda-escolar/id1194790287",
  };

  const appStoreLink = appStoreLinks[language] || appStoreLinks.en;
  const playStoreLink =
    "https://play.google.com/store/apps/details?id=br.com.agenda1.app";

  const handlePrint = () => {
    window.print();
  };

  const handleOpenSchool = () => {
    analytics.openSchoolClick(schoolSku);
    // Use custom scheme to open the app directly
    // Format: agenda1://{schoolSku}?name=...&lang=...
    // The app's deep link handler extracts the school SKU from the hostname
    window.location.href = customSchemeLink;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      {/* Full-width header */}
      <div className={`bg-gradient-to-r from-primary-500 to-primary-600 ${mobileOS.isMobile ? 'py-6' : 'py-12'} px-4 text-white print:bg-primary-500`}>
        <div className="max-w-4xl mx-auto">
          <div className={`flex items-center justify-center ${mobileOS.isMobile ? 'gap-4' : 'gap-6'}`}>
            {/* Logo */}
            <div className="flex-shrink-0">
              <img
                src="/logo.svg"
                alt="Agenda1"
                className={mobileOS.isMobile ? "w-12 h-12" : "w-16 h-16 md:w-20 md:h-20"}
              />
            </div>
            {/* Text content - aligned left */}
            <div className="text-left">
              <h1 className={`${mobileOS.isMobile ? 'text-2xl' : 'text-4xl md:text-5xl'} font-bold mb-1 print:text-5xl`}>
                {schoolName}
              </h1>
              <p className={`text-white/90 ${mobileOS.isMobile ? 'text-base' : 'text-xl'}`}>{t.schoolSubtitle}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className={`max-w-4xl mx-auto px-4 ${mobileOS.isMobile ? 'py-4' : 'py-12'}`}>
        <div className={`bg-white shadow-xl print:shadow-none ${mobileOS.isMobile ? 'rounded-xl' : ''}`}>
          <div className={mobileOS.isMobile ? "p-4" : "p-8 md:p-12"}>
            <div className="flex flex-col items-center space-y-8">
              {/* QR Code - Desktop only */}
              {!mobileOS.isMobile && (
                <>
                  <div className="bg-white p-6 shadow-lg print:shadow-none">
                    <QRCodeSVG
                      value={deepLink}
                      size={280}
                      level="H"
                      includeMargin={true}
                      className="w-full h-auto max-w-[280px]"
                    />
                  </div>
                  <p className="text-gray-700 text-lg font-medium">
                    {t.scanQrCode}
                  </p>
                </>
              )}

              <div className="text-center space-y-4 w-full print:hidden">
                {!mobileOS.isMobile && (
                  <>
                    <div className="flex items-center justify-center space-x-4 my-6">
                      <div className="h-px bg-gray-300 flex-1"></div>
                      <span className="text-gray-500 text-sm">{t.or}</span>
                      <div className="h-px bg-gray-300 flex-1"></div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4">
                      {t.downloadApp}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                      <a
                        href={appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                        onClick={() => analytics.appStoreClick('ios', schoolSku)}
                      >
                        <img
                          src={t.appStoreBadgeUrl}
                          alt={t.appStoreBadgeAlt}
                          className="h-12 w-auto"
                        />
                      </a>
                      <a
                        href={playStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                        onClick={() => analytics.appStoreClick('android', schoolSku)}
                      >
                        <img
                          src={t.playStoreBadgeUrl}
                          alt={t.playStoreBadgeAlt}
                          className="h-[68px] w-auto"
                        />
                      </a>
                    </div>
                  </>
                )}

                {mobileOS.isMobile && (
                  <div className="w-full space-y-3">
                    {/* Step 1: Download the App */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-bold text-sm">1</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-base mb-0.5">
                            {t.step1Title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-3">
                            {t.step1Description}
                          </p>
                          {/* Official Store Badge */}
                          <a
                            href={mobileOS.isIOS ? appStoreLink : playStoreLink}
                            className="inline-block"
                            onClick={() => analytics.appStoreClick(mobileOS.isIOS ? 'ios' : 'android', schoolSku)}
                          >
                            <img
                              src={mobileOS.isIOS ? t.appStoreBadgeUrl : t.playStoreBadgeUrl}
                              alt={mobileOS.isIOS ? t.appStoreBadgeAlt : t.playStoreBadgeAlt}
                              className={mobileOS.isIOS ? "h-11 w-auto" : "h-[46px] w-auto"}
                            />
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Step 2: Open the School */}
                    <div className="bg-primary-50 rounded-xl p-4 border-2 border-primary-200">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">2</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-base mb-0.5">
                            {t.step2Title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-3">
                            {t.step2Description}
                          </p>
                          <button
                            onClick={handleOpenSchool}
                            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                          >
                            <img
                              src="/logo.svg"
                              alt=""
                              className="w-5 h-5 brightness-0 invert"
                            />
                            {t.openSchoolButton}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!mobileOS.isMobile && (
                  <>
                    <div className="flex items-center justify-center space-x-4 my-6">
                      <div className="h-px bg-gray-300 flex-1"></div>
                      <span className="text-gray-500 text-sm">{t.or}</span>
                      <div className="h-px bg-gray-300 flex-1"></div>
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={handlePrint}
                        className="w-full sm:w-auto bg-gray-700 hover:bg-gray-800 text-white font-semibold py-4 px-8 rounded-xl transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {t.printQrCode}
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="text-center text-sm text-gray-500 print:hidden">
                <p>
                  {t.schoolCode}:{" "}
                  <span className="font-mono font-semibold text-gray-700">
                    {schoolSku}
                  </span>
                </p>
              </div>

              <div className="hidden print:block text-center space-y-2">
                <p className="text-gray-700 text-base">{t.scanInstruction}</p>
                <p className="text-gray-600 text-sm">
                  {t.accessUrl}:{" "}
                  <span className="font-semibold">{deepLink}</span>
                </p>
                <p className="text-gray-500 text-xs mt-4">
                  {t.schoolCode}: {schoolSku}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-8 py-6 text-center border-t border-gray-200 print:hidden">
            <p className="text-gray-600 text-sm">
              {t.needHelp}:{" "}
              <a
                href={`mailto:${t.support}`}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                {t.support}
              </a>
            </p>
          </div>
        </div>

        <div className="text-center mt-6 print:hidden">
          <p className="text-primary-800 text-sm">{t.copyright}</p>
        </div>
      </div>
    </div>
  );
}

export default App;
